import { useSeo } from "@/lib/seo";
import { getMeta } from "@/seo/pages";
import { trackPage } from "@/lib/track";
import { useEffect } from "react";
import "@/styles/home.css";

const SITE = "https://99tests.com";

const TIMELINE = [
  ["Minute 0", "Pay", "Choose a package, pay by card. Invoice issued at checkout."],
  ["Minute 2", "Submit", "Build link or web URL, test cases in any format, device and browser targets, deadline."],
  ["Minute 5", "Plan", "Cases parsed into sessions by device. You see exactly what will run."],
  ["Hour 1", "Dispatch", "Offers go to testers who own your target phones and laptops. First to accept takes the session."],
  ["Day 1–2", "Sessions", "Timed 45- or 90-minute sessions. Findings recorded live with media."],
  ["Day 3", "Verify and deliver", "Findings checked, merged, graded. Report emailed as page and PDF."],
];

export const SERVICES = {
  "mobile-app-testing-india": {
    name: "Mobile app testing in India",
    h1a: "Mobile app testing on real devices",
    h1b: "in India.",
    kicker: "Android & iOS · real phones · real networks",
    breaksEyebrow: "What breaks on real Indian devices",
    breaksH2: "The bugs an emulator will never show you.",
    lede: "Your Android and iOS build, run by real people on the phones your Indian users actually own — Redmi, Realme, Galaxy, Vivo and iPhone, on Jio and Airtel networks. Upload your test cases, pay a fixed price, and get a verified pass/fail report in 3 business days.",
    breaks: [
      ["MIUI and ColorOS kill your background service", "Aggressive battery management on Xiaomi and Oppo phones stops background tasks, so uploads, sync and push notifications die silently. It never reproduces on an emulator."],
      ["Checkout drops on the switch to a UPI app", "On a budget Android with low RAM, backgrounding your app to approve a UPI payment can kill it — the single most common S1 our testers find, and the one that costs you a sale."],
      ["Jio and Airtel are not office Wi-Fi", "Real carrier networks drop mid-request, throttle uploads and time out differently across cities. Findings on real SIMs surface what data-centre throttling can't."],
      ["The screen your user has is not the one you designed on", "1600×720 budget panels, notches, small viewports and system font scaling break layouts that looked fine on your flagship test device."],
      ["iOS is its own set of surprises", "Permission prompts, Safari webviews and iOS 17 vs 18 differences on iPhone 11 and 13 behave differently from a clean simulator."],
    ],
    scopeIn: [
      "Execution of your Android (APK, Play internal testing, TestFlight) and iOS test cases on real, tester-owned phones",
      "Exploratory testing around those cases within the session charter",
      "Findings with steps, expected, actual, device, OS, network and a screen recording on serious bugs",
      "Coverage across Xiaomi/MIUI, Oppo/ColorOS, Samsung One UI, Vivo Funtouch, realme UI and iPhone",
      "Re-runs of the same plan on a new build",
    ],
    scopeOut: [
      "Not test automation, and no substitute for it",
      "Not a security or penetration test",
      "Not a certification or an audit under any standard",
      "Not performance or load testing",
      "Writing your test cases — bring your own, AI-generated or otherwise",
    ],
    faq: [
      ["Which phones do you cover?", "Budget and mid-range Android from Xiaomi/Redmi, Realme, Samsung, Vivo and Oppo, plus iPhone 11 and 13 — the devices most Indian users actually carry. You set the targets by brand and minimum OS; we match testers who own them."],
      ["How do I share my Android or iOS build?", "An APK download link, a Google Play internal testing link, a TestFlight public link, or a staging URL for a mobile web app. Test credentials go in the submission form and are shown only to assigned testers."],
      ["Do you test on real SIM networks?", "Yes. Testers are on their own Jio, Airtel and Vi connections in their own cities — not a data-centre link with simulated throttling. The session records the network, and the report shows it."],
      ["How fast is the report?", "Starter and Standard reports arrive in 3 business days from payment; the Deep package with 90-minute sessions is 5 business days. No onboarding call, no scoping document."],
      ["Do you write the test cases?", "No. You bring your own — human- or AI-written — in CSV, Excel, Sheets, Word, Markdown, or TestRail/Jira exports. We parse them into sessions and show you the coverage before testers start."],
      ["Do you invoice with GST?", "Yes, for Indian customers — enter your GSTIN at checkout and Stripe issues a compliant invoice immediately. Customers outside India are billed in US dollars."],
    ],
    related: [
      ["Crowdsourced testing in India", "/crowdsourced-testing"],
      ["See packages and pricing", "/pricing"],
      ["Read a sample report", "/sample-report/"],
      ["Real-device testing in India", "/real-device-testing-india"],
      ["99tests vs BrowserStack App Live", "/vs/browserstack-app-live"],
    ],
  },
  "cross-browser-testing": {
    name: "Cross-browser testing on real machines",
    h1a: "Cross-browser testing on",
    h1b: "real laptops and phones.",
    kicker: "Chrome · Safari · Edge · Firefox · real machines",
    breaksEyebrow: "What breaks across browsers",
    breaksH2: "The bugs one browser will never show you.",
    lede: "Your web app, opened by real people on the browsers and machines your customers actually use — Chrome and Edge on Windows, Safari on a Mac, Firefox, and mobile Safari and Chrome on real phones. Upload your test cases, pay a fixed price, and get a verified pass/fail report in 3 business days.",
    breaks: [
      ["Safari is not Chrome", "The popup your OAuth flow opens, the date input, the video autoplay policy and the flexbox quirk all behave differently in Safari and WebKit. Testing only in Chrome hides them until a Mac user complains."],
      ["The five-year-old office laptop", "A 2019 Windows laptop on an older Chrome at 1366×768 scrolls, wraps and renders differently from your 27-inch dev monitor — and that's the machine your enterprise customer actually uses."],
      ["Mobile browsers are their own browsers", "Mobile Safari and Android Chrome handle viewport units, sticky headers, file uploads and on-screen keyboards differently from desktop, and from each other."],
      ["Extensions, zoom and system fonts", "Real machines carry ad blockers, 125% display scaling and custom fonts that break layouts a clean VM never reveals."],
    ],
    scopeIn: [
      "Execution of your test cases on Chrome, Edge, Firefox and Safari on real Windows, macOS and ChromeOS machines",
      "Mobile web on real iOS Safari and Android Chrome",
      "Findings with steps, expected, actual, browser version, OS, viewport, network and a screen recording on serious bugs",
      "Exploratory testing around those cases within the session charter",
      "Re-runs of the same plan on a new build",
    ],
    scopeOut: [
      "Not test automation, and no substitute for it",
      "Not a security or penetration test",
      "Not a certification or an audit under any standard",
      "Not performance or load testing",
      "Writing your test cases — bring your own, AI-generated or otherwise",
    ],
    faq: [
      ["Which browsers and versions do you cover?", "Chrome, Edge, Firefox and Safari on Windows, macOS and ChromeOS, on testers' own machines — so you get the versions and update states real users are on, not a single pinned build. The session records the exact browser version and viewport."],
      ["Do you test mobile browsers too?", "Yes — mobile Safari on iPhone and Chrome on Android, on real phones. You set the browser and OS targets the same way you pick a desktop browser."],
      ["How do I give you the URL and credentials?", "Enter your staging or production URL in the submission form, with any test login or basic-auth credentials. They are shown only to assigned testers and not stored beyond the job."],
      ["How fast is the report?", "Starter and Standard reports arrive in 3 business days from payment; Deep is 5. No onboarding call."],
      ["Do you write the test cases?", "No. Bring your own — human- or AI-written — in CSV, Excel, Sheets, Word, Markdown, or TestRail/Jira exports. We parse them and show you the coverage before testers start."],
    ],
    related: [
      ["See packages and pricing", "/pricing"],
      ["Read a sample report", "/sample-report/"],
      ["Real-device testing in India", "/real-device-testing-india"],
      ["99tests vs device clouds & automation", "/vs-device-clouds-and-automation"],
    ],
  },
  "exploratory-testing": {
    name: "Exploratory testing by real testers",
    h1a: "Exploratory testing by",
    h1b: "real people on real devices.",
    kicker: "Charter-driven · real devices · verified findings",
    breaksEyebrow: "What scripts miss",
    breaksH2: "The bugs a script never describes.",
    lede: "Beyond your scripted cases: real testers probe your app the way your users will — on the phones, laptops and networks they own — and report what breaks, with steps and a screen recording. Fixed price, verified report in 3 business days.",
    breaks: [
      ["The path you didn't write a case for", "Users don't follow your happy path. Exploratory sessions find the dead end, the double-tap, the back-button state and the empty-cart edge your test suite never described."],
      ["Real-world device state", "A full phone, low storage, thirty background apps and battery-saver on — the conditions where bugs actually live, not a clean lab unit."],
      ["Interrupted flows", "A call mid-checkout, a network drop, an app switch to a UPI app and back. Real interruptions surface real crashes."],
      ["Confusing, not just broken", "Testers flag what's misleading or hard to use, not only what throws an error — usability findings a pass/fail script can't capture."],
    ],
    scopeIn: [
      "Charter-driven exploratory sessions on real, tester-owned phones, laptops and desktops",
      "Execution of any scripted cases you provide alongside the exploration",
      "Findings with steps, expected, actual, device, OS, network and a screen recording on serious bugs",
      "Severity graded against a published rubric; duplicates merged; incomplete reports rejected",
      "Re-runs of the same charter on a new build",
    ],
    scopeOut: [
      "Not test automation, and no substitute for it",
      "Not a security or penetration test",
      "Not a certification or an audit under any standard",
      "Not performance or load testing",
      "Writing your test charter for you from scratch — you set the focus, we explore it",
    ],
    faq: [
      ["How is exploratory testing different from running my test cases?", "Scripted cases check the paths you already thought of. Exploratory sessions send skilled testers to poke at everything around them — edge cases, odd inputs, interrupted flows — and report what breaks or confuses, with evidence."],
      ["Can I steer what testers focus on?", "Yes. You provide a short charter — the areas, flows or risks you care about — and sessions are timeboxed against it. You can also attach scripted cases to run alongside."],
      ["Do findings come with evidence?", "Every serious finding has steps, expected, actual, device, OS, network and a screen recording. Incomplete reports are rejected and duplicates merged before you see them."],
      ["How fast is the report?", "Starter and Standard reports arrive in 3 business days from payment; Deep, with 90-minute sessions, is 5."],
      ["Do you also run my scripted cases?", "Yes — you can mix scripted execution and exploration in the same job, and the report shows coverage for both."],
    ],
    related: [
      ["See packages and pricing", "/pricing"],
      ["Read a sample report", "/sample-report/"],
      ["Real-device testing in India", "/real-device-testing-india"],
      ["Mobile app testing in India", "/mobile-app-testing-india"],
    ],
  },
};

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a href="#faq">Questions</a>
        <a className="btn sm blue" href="/pricing" data-testid="service-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div data-testid="service-footer-explore">
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/pricing">Pricing</a>
        <a href="/sample-report/">Sample report</a>
        <a href="/real-device-testing-india">Real-device testing in India</a>
        <a href="/test-ai-generated-test-cases">Test AI-generated test cases</a>
      </div>
      <div>
        <a href="/">Home</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function Service({ slug }) {
  const s = SERVICES[slug];
  const path = `/${slug}`;
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: s.name, item: SITE + path },
      ] },
      { "@type": "HowTo", name: "How a 99tests job works", step: TIMELINE.map(([, name, text]) => ({ "@type": "HowToStep", name, text })) },
      { "@type": "FAQPage", mainEntity: s.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };
  useSeo({ ...getMeta(path), path, jsonld });
  useEffect(() => { trackPage(path); }, [path]);

  return (
    <>
      <Header />
      <main data-testid={`service-${slug}`}>
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Fixed price</b><span className="dot"></span><b>3 business days</b><span className="dot"></span><span>{s.kicker}</span></div>
              <h1>{s.h1a} <em>{s.h1b}</em></h1>
              <p className="lede">{s.lede}</p>
              <div className="actions">
                <a className="btn blue" href="/pricing" data-testid="service-packages-btn">See packages</a>
                <a className="btn ghost" href="/sample-report/" data-testid="service-sample-report-link">Read a sample report</a>
              </div>
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">{s.breaksEyebrow}</span><h2>{s.breaksH2}</h2></div>
            <div className="proof" data-testid="service-breaks">
              {s.breaks.map(([h, p], i) => (<div key={i}><h4>{h}</h4><p>{p}</p></div>))}
            </div>
          </div>
        </section>

        <section id="how">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Three business days</span><h2>From payment to report.</h2></div>
            <div className="tl">
              {TIMELINE.map((t, i) => (<div key={i}><b>{t[0]}</b><h4>{t[1]}</h4><p>{t[2]}</p></div>))}
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="scope" data-testid="service-scope">
              <div>
                <h3>In scope</h3>
                <ul>{s.scopeIn.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </div>
              <div>
                <h3>Not in scope — stated plainly</h3>
                <ul>{s.scopeOut.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </div>
            </div>
          </div>
        </section>

        <section id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="service-faq">
              {s.faq.map(([q, a], i) => (
                <details className="f" key={i} open={i === 0}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Related</span><h2>Keep reading.</h2></div>
            <div data-testid="service-related" style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {s.related.map(([label, href], i) => (
                <a key={i} className="btn ghost" href={href}>{label}</a>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Ship this release knowing it works on a Redmi on Jio.</h2>
                <p>Pay, upload your test cases, and testers are on it within the hour. Verified report in three business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
